// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { Zap, Shield, AlertTriangle, Play, Pause, Settings, Clock, CheckCircle, Target, Activity, Filter, Download, Eye, MoreVertical, RefreshCw, Brain, Search } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const RaspunsAutomatizat = () => {
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data pentru răspuns automatizat
  const automationData = {
    overview: {
      activePlaybooks: 15,
      executionsToday: 47,
      successRate: 96.8,
      avgResponseTime: '2.3s',
      incidentsHandled: 234,
      manualInterventions: 8
    },
    playbooks: [
      {
        id: 'PB-001',
        name: 'Malware Detection Response',
        category: 'Malware',
        trigger: 'Malware Detected',
        priority: 'Critical',
        status: 'Active',
        description: 'Răspuns automat la detectarea malware: izolare, carantină și scanare.',
        steps: 8,
        avgExecutionTime: '45s',
        successRate: 98.2,
        lastExecution: '2024-03-15 14:32:15',
        executionCount: 23,
        automationLevel: 'Full'
      },
      {
        id: 'PB-002',
        name: 'Brute Force Attack Mitigation',
        category: 'Intrusion',
        trigger: 'Failed Login Attempts > 5',
        priority: 'High',
        status: 'Active',
        description: 'Blocare automată IP-uri cu tentative repetate de autentificare.',
        steps: 5,
        avgExecutionTime: '12s',
        successRate: 99.1,
        lastExecution: '2024-03-15 13:45:22',
        executionCount: 67,
        automationLevel: 'Full'
      },
      {
        id: 'PB-003',
        name: 'Data Exfiltration Prevention',
        category: 'Data Breach',
        trigger: 'Unusual Data Transfer',
        priority: 'Critical',
        status: 'Active',
        description: 'Prevenire automată a exfiltrării de date: blocare transfer și investigație.',
        steps: 10,
        avgExecutionTime: '90s',
        successRate: 94.7,
        lastExecution: '2024-03-15 12:18:45',
        executionCount: 12,
        automationLevel: 'Semi-Auto'
      }
    ],
    metrics: [
      { category: 'Response Time', metric: '2.3 seconds', trend: 'improving', status: 'good' },
      { category: 'Success Rate', metric: '96.8%', trend: 'stable', status: 'good' },
      { category: 'False Positives', metric: '2.1%', trend: 'improving', status: 'excellent' },
      { category: 'Manual Interventions', metric: '8 today', trend: 'stable', status: 'good' }
    ]
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Paused': return 'warning';
      case 'Draft': return 'info';
      case 'Disabled': return 'error';
      default: return 'default';
    }
  };

  const getAutomationColor = (level) => {
    switch (level) {
      case 'Full': return 'success';
      case 'Semi-Auto': return 'warning';
      case 'Manual': return 'info';
      default: return 'default';
    }
  };

  const filteredPlaybooks = automationData.playbooks.filter(playbook =>
    playbook.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    playbook.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const playbookColumns = [
    { 
      header: 'Playbook', 
      key: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-xs text-gray-500">{row.id} • {row.category}</div>
        </div>
      )
    },
    { 
      header: 'Trigger', 
      key: 'trigger'
    },
    { 
      header: 'Prioritate', 
      key: 'priority',
      render: (value) => <Badge variant={getPriorityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Automatizare', 
      key: 'automationLevel',
      render: (value) => <Badge variant={getAutomationColor(value)}>{value}</Badge>
    },
    { 
      header: 'Success Rate', 
      key: 'successRate',
      render: (value) => <span className="font-semibold text-green-600">{value}%</span>
    },
    { 
      header: 'Execuții', 
      key: 'executionCount'
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <button
          onClick={() => {
            setSelectedPlaybook(row);
            setShowPlaybookModal(true);
          }}
          className="text-primary-600 hover:text-primary-800"
        >
          <Eye className="w-4 h-4" />
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Răspuns Automatizat</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Protocoale automate de răspuns la incidente de securitate cu orchestrare inteligentă
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            className={`btn-secondary ${automationEnabled ? 'bg-green-100' : 'bg-red-100'}`}
            onClick={() => setAutomationEnabled(!automationEnabled)}
          >
            {automationEnabled ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {automationEnabled ? 'Pause' : 'Resume'}
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowConfigModal(true)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurare
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <Card title="Playbooks Active" icon={Zap}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.activePlaybooks}</div>
        </Card>
        <Card title="Execuții Astăzi" icon={Activity}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.executionsToday}</div>
        </Card>
        <Card title="Success Rate" icon={Target}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.successRate}%</div>
        </Card>
        <Card title="Timp Răspuns" icon={Clock}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.avgResponseTime}</div>
        </Card>
        <Card title="Incidente Handled" icon={CheckCircle}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.incidentsHandled}</div>
        </Card>
        <Card title="Intervenții Manuale" icon={AlertTriangle}>
          <div className="text-2xl font-bold text-primary-600">{automationData.overview.manualInterventions}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Playbooks Table */}
        <div className="lg:col-span-3">
          <Card title="Security Playbooks" subtitle="Protocoale automate de răspuns la incidente">
            <div className="mb-4 flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută playbook..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <button className="btn-secondary">
                <Filter className="w-4 h-4 mr-2" />
                Filtre
              </button>
            </div>
            <Table columns={playbookColumns} data={filteredPlaybooks} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Metrici Performanță" icon={Target}>
            <div className="space-y-4">
              {automationData.metrics.map((metric, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                    {metric.category}
                  </div>
                  <div className="text-lg font-bold text-primary-600">
                    {metric.metric}
                  </div>
                  <div className="text-xs text-gray-500">
                    {metric.trend}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Playbook Detail Modal */}
      <Modal
        isOpen={showPlaybookModal}
        onClose={() => {
          setShowPlaybookModal(false);
          setSelectedPlaybook(null);
        }}
        title={`Playbook: ${selectedPlaybook?.name}`}
        size="large"
      >
        {selectedPlaybook && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>ID:</span>
                    <span>{selectedPlaybook.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Categorie:</span>
                    <span>{selectedPlaybook.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Prioritate:</span>
                    <Badge variant={getPriorityColor(selectedPlaybook.priority)}>{selectedPlaybook.priority}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <Badge variant={getStatusColor(selectedPlaybook.status)}>{selectedPlaybook.status}</Badge>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Performance</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium">Success Rate</div>
                    <div className="text-xl font-bold text-blue-600">{selectedPlaybook.successRate}%</div>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-sm font-medium">Execuții</div>
                    <div className="text-xl font-bold text-green-600">{selectedPlaybook.executionCount}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{selectedPlaybook.description}</p>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Editează</button>
              <button className="btn-primary">Test Execution</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configurare Automation Engine"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Setări Generale</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Auto-response Critical Threats</span>
                <input type="checkbox" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span>Email Notifications</span>
                <input type="checkbox" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span>SIEM Integration</span>
                <input type="checkbox" defaultChecked />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Anulează
            </button>
            <button className="btn-primary">
              Salvează
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RaspunsAutomatizat;